import React from "react";
import { ALIAS_PATTERN, ALIAS_VALIDATION_MESSAGE } from "constants/validation";
import {
  APP_ALIAS_MIN_CHAR_COUNT,
  APP_PUBLISH_INTENT
} from "./constants/application";
import { FORM_KEY } from "./constants/form";
import { STATUS } from "./constants/ui";
import { ensureLeadingSlash } from "./route";
import { formFactorAction } from "./view";
import { EXCLUSIVE_PATHS_MAP } from "./paths";
import trimStart from "lodash/trimStart";
import startCase from "lodash/startCase";
import compact from "lodash/compact";
import kebabCase from "lodash/kebabCase";
import { reduceIdMap } from "./data";

const DUPLICATE = "duplicate";
const RESERVED = "reserved";
const INVALID = "invalid";
export const PUBLISH_VALIDATION = {
  DUPLICATE,
  RESERVED,
  INVALID
};

const COMMON_ALTERNATIVES = {
  checkout: ["checkout-now"],
  pricing: ["prices", "pricing-table", "product-pricing", "our-prices"],
  buy: ["buy-now"],
  pay: ["pay-now"],
  purchase: ["purchase-now"]
};

const DATE_KEYS = {
  checkout: true,
  pricing: true
};

const QUARTER_MAP = {
  0: 1,
  1: 1,
  2: 1,
  3: 2,
  4: 2,
  5: 2,
  6: 3,
  7: 3,
  8: 3,
  9: 4,
  10: 4,
  11: 4
};

const getAlternatives = (route) => {
  let result = [];

  let matchKey;
  for (const key in COMMON_ALTERNATIVES) {
    const hasMatch = new RegExp(key).test(route);
    if (hasMatch) {
      matchKey = key;
      result = result.concat(COMMON_ALTERNATIVES[key]);
      break;
    }
  }
  if (DATE_KEYS[matchKey]) {
    const today = new Date();

    result = result.concat([
      `${route}-${today.getFullYear()}`,
      `${route}-Q${QUARTER_MAP[today.getMonth()]}`
    ]);
  }

  return result;
};

export const getPublishRouteOmissions = (routes) => {
  const result = {};
  for (const route in routes) {
    const preparedRoute = trimStart(route, "/");
    if (preparedRoute) {
      result[preparedRoute] = true;
    }
  }
  return result;
};

export const getProductPrimariesRouteSuggestions = ({
  products,
  primaries
}) => {
  const productMap = reduceIdMap(products || []);

  return Array.isArray(primaries)
    ? primaries.reduce((memo, { product }) => {
        const productMatch = productMap[product];
        if (productMatch && productMatch.name) {
          memo.push(kebabCase(productMatch.name));
        }
        return memo;
      }, [])
    : [];
};

const addMinSuggestions = ({ result, suggestions, omissions }) => {
  for (let index = 0; index < suggestions.length; index++) {
    const suggestion = suggestions[index];
    let counter = 1;
    while (counter < 10) {
      counter++;
      const genRoute = `${suggestion}-${counter}`;
      if (!omissions[genRoute]) {
        result.push(genRoute);
        break;
      }
    }
  }
};

export const getRouteSuggestions = ({
  suggestions,
  route,
  errorType,
  omissions
}) => {
  const ctxRoute = trimStart(route);
  const alternatives = getAlternatives(ctxRoute);
  const allSuggestions = compact([...suggestions, ...alternatives]);
  if (errorType && errorType === PUBLISH_VALIDATION.DUPLICATE) {
    allSuggestions.push(`${ctxRoute}-2`);
  }

  /**
   * Only return routes not contained within omissions (i.e. existing routes)
   */
  let result = allSuggestions;

  if (omissions) {
    result = allSuggestions.filter((suggestion) => !omissions[suggestion]);
  }

  const shouldAddMinSuggestions =
    Array.isArray(result) &&
    !result.length &&
    !alternatives.length &&
    allSuggestions.length > 0 &&
    Boolean(omissions);
  if (shouldAddMinSuggestions) {
    addMinSuggestions({ result, suggestions, allSuggestions, omissions });
  }
  return result;
};

export const getPublishInfo = ({
  deployedAtTarget,
  replacementDeploy,
  manifestLiveVariation,
  targetRoute
}) => {
  const result = {
    icon: "help",
    copy: ""
  };
  if (!targetRoute) {
    result.copy = `Set a location to publish to.`;
  } else if (deployedAtTarget) {
    result.copy = `This page is deployed at ${targetRoute}. Change the target location if you want to deploy it elsewhere.`;
  } else if (replacementDeploy) {
    result.icon = "alert";
    result.copy = `Warning this will replace the page you currently have published at ${targetRoute}. Are you sure you want to do this? ${formFactorAction()} publish to confirm.`;
  } else if (manifestLiveVariation && manifestLiveVariation.route) {
    result.copy = `You are moving this page from ${ensureLeadingSlash(
      manifestLiveVariation.route
    )} to ${ensureLeadingSlash(
      targetRoute
    )}. ${formFactorAction()} publish to confirm.`;
  }

  return result.copy ? result : null;
};

/**
 * Is published
 * - replace an occupied route
 * - move to an unoccupied route
 * Not published
 * - replace an occupied route
 * - publish to an unoccupied route
 */
export const getPublishPhases = ({
  urlRoot,
  replacementDeploy,
  manifestLiveVariation,
  targetRoute,
  error,
  errorType,
  touched
}) => {
  const phases = [];
  const deployedRoute = manifestLiveVariation && manifestLiveVariation.route;
  const rootTarget = targetRoute.replace(/^\//, "");
  const fullTargetRoute = `${urlRoot}${targetRoute}`;

  if (deployedRoute) {
    const fullDeployedRoute = `${urlRoot}${deployedRoute}`;
    phases.push({
      fieldLabel: "Current",
      icon: "location",
      optionLabel: (
        <a
          target="_blank"
          rel="noreferrer"
          className="link color-primary truncate fw5 link__decoration--hover"
          href={fullDeployedRoute}
        >
          {fullDeployedRoute}
        </a>
      )
    });
  } else {
    phases.push({
      fieldLabel: "Unpublished",
      status: STATUS.NEUTRAL,
      icon: "subtract",
      optionLabel: "Set a location to publish this page to"
    });
  }

  if (touched) {
    if (rootTarget) {
      if (error) {
        phases.push({
          status: STATUS.DANGER,
          fieldLabel: errorType ? startCase(errorType) : "Invalid",
          icon: "alert",
          optionLabel: error
        });
      } else {
        if (replacementDeploy) {
          if (deployedRoute === targetRoute) {
            phases.push({
              fieldLabel: "Conflict",
              status: STATUS.WARNING,
              icon: "alert",
              optionLabel: `This page is already deployed at ${targetRoute}.`
            });
          } else {
            phases.push({
              fieldLabel: "Replace",
              icon: "replace",
              status: STATUS.WARNING,
              optionLabel: [
                `Replace the page currently published at:`,
                <a
                  key="replace-link"
                  target="_blank"
                  rel="noreferrer"
                  className="link color-primary truncate fw5 link__decoration--hover"
                  href={fullTargetRoute}
                >
                  {fullTargetRoute}
                </a>,
                /**
                 * If this manifest is already deployed at a route
                 * Then tell the user that it will be deactivated by the publish action
                 */
                deployedRoute
                  ? `Note: the page published at ${deployedRoute} will also be deactivated.`
                  : ""
              ]
            });
          }
        } else {
          phases.push({
            fieldLabel: deployedRoute ? "Move to" : "Publish to",
            status: STATUS.SUCCESS,
            icon: "check",
            optionLabel: fullTargetRoute
          });
        }
      }
    } else {
      phases.push({
        fieldLabel: "Location required",
        status: STATUS.WARNING,
        icon: "alert"
      });
    }
  }

  return phases;
};

export const validateNewPublishRoute = ({ route, routes }) => {
  let result = null;
  if (route) {
    const valid = ALIAS_PATTERN.test(route);
    const minMet = route.length >= APP_ALIAS_MIN_CHAR_COUNT;

    const routeOnly = trimStart(route, "/");
    const reservedRoute = EXCLUSIVE_PATHS_MAP[routeOnly];

    const ctxRoute = ensureLeadingSlash(route);
    const routeTaken = routes && routes[ctxRoute];
    if (routeTaken) {
      result = {
        type: PUBLISH_VALIDATION.DUPLICATE,
        message: `${ctxRoute} already exists.`
      };
    } else if (!minMet) {
      result = {
        type: PUBLISH_VALIDATION.INVALID,
        message: `Location must be at least 3 characters long. ${ALIAS_VALIDATION_MESSAGE}`
      };
    } else if (reservedRoute) {
      result = {
        type: PUBLISH_VALIDATION.RESERVED,
        message: `${ctxRoute} is reserved. Try a different location.`
      };
    } else if (!valid) {
      result = {
        type: PUBLISH_VALIDATION.INVALID,
        message: ALIAS_VALIDATION_MESSAGE
      };
    }
  } else {
    result = result = {
      type: PUBLISH_VALIDATION.INVALID,
      message: "Location is required"
    };
  }

  return result;
};

export const validatePublishLocationSchema = async (values) => {
  let result = null;

  if (values.intent === APP_PUBLISH_INTENT.NEW) {
    const newValidated = validateNewPublishRoute({
      route: values.newRoute,
      routes: values[FORM_KEY] && values[FORM_KEY].existingRoutes
    });
    if (newValidated) {
      result = {
        newRoute: newValidated.message,
        type: newValidated.type
      };
    }
  } else if (values.intent === APP_PUBLISH_INTENT.EXISTING) {
    if (!values.existingRoute) {
      result = {
        existingRoute: "Location required for publishing.",
        type: PUBLISH_VALIDATION.INVALID
      };
    }
  }

  return result;
};
